window.global ||= window; // https://github.com/vitejs/vite/issues/2778#issuecomment-810086159

import React from "react";
import { createRoot } from "react-dom/client";

import { StyledEngineProvider, ThemeProvider } from "@mui/material/styles";
import { LicenseInfo } from "@mui/x-data-grid-pro";
import { StackletUIThemeProvider, theme } from "@stacklet/ui";
import { RouterProvider } from "react-router";

import { BootstrapErrorFallback } from "app/components/fallbacks";
import { ConfigProvider } from "app/contexts";
import { ApiExplorerProvider } from "app/contexts/ApiExplorerContext";
import { ThemeProvider as ColorSchemeProvider } from "app/contexts/ThemeContext";
import { primeSubscriptionClient } from "app/utils/subscriptionClient";

import { reportWebVitals } from "./reportWebVitals";
import { createAppRouter } from "./Routes";
import { startConsole } from "./startup";

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_X_LICENSE ?? "");

// Resolve the config and build the subscriptions client once at startup, so
// the first view to subscribe only has the socket left to open. graphql-ws
// connects lazily, on that first subscription.
// Called here rather than at RelayEnvironment's module scope: that module is
// imported by component tests, and a connect-and-retry-forever side effect on
// import made the suite flaky.
primeSubscriptionClient();

// Supports weights 100-900
import "@fontsource-variable/dm-sans";
// Weight 400 only
import "@fontsource/dm-mono";

const container = document.getElementById("root");
// eslint-disable-next-line typescript/no-non-null-assertion
const root = createRoot(container!);

function renderApp() {
  const router = createAppRouter();

  root.render(
    <React.StrictMode>
      <ConfigProvider>
        <ApiExplorerProvider>
          <ColorSchemeProvider>
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={theme}>
                <StackletUIThemeProvider>
                  <RouterProvider router={router} />
                </StackletUIThemeProvider>
              </ThemeProvider>
            </StyledEngineProvider>
          </ColorSchemeProvider>
        </ApiExplorerProvider>
      </ConfigProvider>
    </React.StrictMode>,
  );
}

startConsole({
  app: renderApp,
  error: () => root.render(<BootstrapErrorFallback />),
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
